import { ViewerQuery } from 'graphql/documents'
import useToasts from 'hooks/useToasts'
import { useState } from 'react'
import {
  useSignProposalMutation,
  Collective,
  CollectiveProposalQueryResult
} from 'graphql/generated'
import {
  getSafe,
  getSafeInstructionsFromCommands,
  parseWeb3ErrorMessage,
  TTxUiState
} from 'lib/collectives/helpers'
import { useWeb3 } from 'context/Web3Context'
import { useQueryErrorHandler } from 'hooks'
import Bugsnag from '@bugsnag/js'

type TCollective = Pick<Collective, 'safeAddress' | 'id'>
type TProposal = NonNullable<
  CollectiveProposalQueryResult['collectiveProposal']
>

interface IProps {
  collective: TCollective
  proposal: TProposal
}

export function useSignProposal({ collective, proposal }: IProps) {
  const { addToast } = useToasts()
  const { signer } = useWeb3()

  const onError = useQueryErrorHandler(`Failed to save proposal signature`)
  const [saveProposalSignature] = useSignProposalMutation({
    onError,
    refetchQueries: [
      {
        query: ViewerQuery
      }
    ]
  })
  const [state, setState] = useState<Maybe<TTxUiState>>(null)

  const signProposal = async () => {
    try {
      if (!signer) {
        addToast('Missing wallet information', {
          appearance: 'warning'
        })
        return
      }
      const safe = await getSafe(collective.safeAddress, signer)

      if (!proposal.hasOnChainCommands) {
        const nonce = await safe.getNonce()
        const payload = {
          timestamp: Date.now(),
          collective: collective.id,
          type: 'signature',
          payload: {
            proposal: proposal.id
          }
        }
        const signature = await signer?.signMessage(JSON.stringify(payload))
        if (!signature) {
          throw new Error(`Invalid signature`)
        }

        await saveProposalSignature({
          variables: {
            proposalId: proposal.id,
            signature,
            nonce
          }
        })

        return setState(TTxUiState.TX_SUCCESS)
      }

      const txPartials = getSafeInstructionsFromCommands(proposal.commands)

      if (!txPartials.length) {
        addToast('Missing execution instructions', {
          appearance: 'warning'
        })
        return
      }

      setState(TTxUiState.WAITING_FOR_PROVIDER)
      const tx = await safe.createTransaction({
        safeTransactionData: txPartials
      })
      const safeTxHash = await safe.getTransactionHash(tx)
      const signature = await safe.signTransactionHash(safeTxHash)
      const nonce = tx.data.nonce

      const txHash = await safe.getTransactionHash(tx)

      setState(TTxUiState.WAITING_FOR_NETWORK)
      await saveProposalSignature({
        variables: {
          proposalId: proposal.id,
          signature: signature.data,
          nonce,
          txHash
        }
      })

      window.analytics?.trackAll(`Proposal - Sign - Complete`, {
        commandTypes: proposal.commands.map(x => x.__typename).join(' + '),
        proposalName: proposal.title,
        proposalId: proposal.id
      })
      setState(TTxUiState.TX_SUCCESS)
    } catch (err) {
      Bugsnag.notify(err)
      addToast(parseWeb3ErrorMessage(err), {
        appearance: 'warning'
      })
      setState(TTxUiState.TX_FAILURE)
      window.analytics?.trackAll(`Proposal - Sign - Error`, {
        commandTypes: proposal.commands.map(x => x.__typename).join(' + '),
        proposalName: proposal.title,
        proposalId: proposal.id
      })
    }
  }

  return { signProposal, state }
}
